import Flickity, { Options as IFlickityOptions } from 'flickity';
import TabIndexUtil from '@frontend/utils/TabIndexUtil';
import { AnchorNavigationEvents } from '@frontend/utils/AnchorNavigationUtil';

export default class BaseSlider {
	public static events = {
		SLIDER_READY: 'BaseSlider.ready',
		SLIDER_CHANGED: 'BaseSlider.changed'
	};

	public static domSelectors = {
		slidesWrapper: '[data-slides-wrapper]',
		prevArrow: '[data-button-prev]',
		nextArrow: '[data-button-next]',
		paginationWrapperOuter: '[data-pagination-wrapper-outer]',
		paginationWrapperInner: '[data-pagination-wrapper-inner]',
		paginationItem: '[data-button-pagination]',
		slidesBeforeInit: '[data-slides-wrapper] > *',
		slidesAfterInit: '.flickity-slider > *'
	};

	private static stateClasses = {
		isActive: 'is-active',
		isSelected: 'is-selected',
		isDragging: 'is-dragging',
		hasSelectedFirstSlide: 'has-selected-first-slide',
		hasSelectedLastSlide: 'has-selected-last-slide',
		hasOverflow: 'has-overflow',
		hasMore: 'has-more'
	};

	private static attributes = {
		paginationIndex: 'data-button-pagination'
	};

	public flickity?: Flickity;

	private currentSlide?: number | null;

	private slideCount?: number;

	private readonly element: HTMLElement;

	private readonly options?: IBaseSliderOptions;

	private initialContentItemId?: string;

	// pagination related variables - start

	private readonly maxDotsVisible = 7;

	private readonly dotWindow = 3;

	private readonly dotDiff = (this.maxDotsVisible - this.dotWindow) / 2;

	private dotWindowMax = this.dotDiff + this.dotWindow - 1; // initial value

	private dotWindowMin = this.dotWindowMax - this.dotWindow + 1; // initial value

	// pagination related variables - end

	public constructor(element: HTMLElement, options?: IBaseSliderOptions) {
		this.element = element;
		this.options = options;
		this.init();
	}

	public init(): void {
		const numSlides = this.element.querySelectorAll(BaseSlider.domSelectors.slidesBeforeInit).length;

		if (numSlides > 1) {
			this.initFlickity();
			this.initEvents();
		}
	}

	public destroy(): void {
		if (this.flickity) {
			this.flickity.destroy();
			this.currentSlide = null;
		}

		document.removeEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler);
	}

	public getElement(): HTMLElement {
		return this.element;
	}

	public prevSlide(): void {
		if (!this.flickity) return;
		this.flickity.stopPlayer();
		this.flickity.previous();
	}

	public nextSlide(): void {
		if (!this.flickity) return;
		this.flickity.stopPlayer();
		this.flickity.next();
	}

	public goToSlide(slide: number): void {
		if (!this.flickity) return;
		this.flickity.stopPlayer();
		this.flickity.select(slide);
	}

	private initFlickity(): void {
		const self = this;
		const optionsString = this.element.getAttribute('data-options');
		const domOptions: IFlickityOptions = optionsString ? JSON.parse(optionsString) : {};
		const wrapper = this.element.querySelector(BaseSlider.domSelectors.slidesWrapper) as HTMLElement;

		BaseSlider.addResizeClassesToFlickity();

		this.flickity = new Flickity(wrapper, {
			wrapAround: true,
			accessibility: false,
			pageDots: false,
			prevNextButtons: false,
			initialIndex: `#${this.initialContentItemId}` as any,
			...(this.options ? this.options.flickityOptions : {}),
			...domOptions,
			on: {
				ready(): void {
					self.slideCount = (this as Flickity).slides.length;
					self.currentSlide = (this as Flickity).selectedIndex;
					(this as Flickity).resize();
					self.updateUI();
					setTimeout((): void => {
						self.emitSlideReady();
						self.emitSlideChange();
					}, 200);
				},
				dragStart(): void {
					self.element.classList.add(BaseSlider.stateClasses.isDragging);
				},
				dragEnd(): void {
					self.element.classList.remove(BaseSlider.stateClasses.isDragging);
				}
			}
		});
	}

	private initEvents(): void {
		const prevArrow: HTMLElement | null = this.element.querySelector(BaseSlider.domSelectors.prevArrow);
		const nextArrow: HTMLElement | null = this.element.querySelector(BaseSlider.domSelectors.nextArrow);
		const dots: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseSlider.domSelectors.paginationItem));

		if (prevArrow && nextArrow) {
			prevArrow.addEventListener('click', (): void => this.prevSlide());
			nextArrow.addEventListener('click', (): void => this.nextSlide());
		}

		dots.forEach((dot): void => {
			dot.addEventListener('click', (): void => {
				this.goToSlide(Number(dot.getAttribute(BaseSlider.attributes.paginationIndex)));
			});
		});

		if (this.flickity) {
			this.flickity.on('select', (index?: number): void => {
				this.currentSlide = index;
				this.updateUI();
				this.emitSlideChange();
			});
		}

		/**
		 * iOS 15 related fix
		 * https://github.com/metafizzy/flickity/issues/1196
		 */
		this.element.addEventListener('touchmove', (event): void => {
			if (this.element.classList.contains(BaseSlider.stateClasses.isDragging)) {
				event.preventDefault();
			}
		});

		this.element.addEventListener('touchend', (): void => {
			this.element.classList.remove(BaseSlider.stateClasses.isDragging);
		});

		// deeplink change
		this.anchorLinkHandler = this.anchorLinkHandler.bind(this);
		document.addEventListener(AnchorNavigationEvents.change, this.anchorLinkHandler);
	}

	private emitSlideChange(): void {
		this.element.dispatchEvent(
			new CustomEvent(BaseSlider.events.SLIDER_CHANGED, {
				detail: {
					currentSlide: this.currentSlide,
					slideCount: this.slideCount,
					slide: this,
					selectedElement: (this.flickity as Flickity).selectedElement
				}
			})
		);
	}

	private emitSlideReady(): void {
		this.element.dispatchEvent(
			new CustomEvent(BaseSlider.events.SLIDER_READY, {
				detail: { slide: this }
			})
		);
	}

	private anchorLinkHandler(event: CustomEventInit): void {
		const target = event.detail.target;
		const id = target.replace('#', '');
		const targetElement = this.element.querySelector(target);

		if (targetElement) {
			if (this.flickity) {
				this.flickity.selectCell(`[data-tabs-pane="${id}"]`);
			}
		}
	}

	private updateUI(): void {
		const dots: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseSlider.domSelectors.paginationItem));
		const slides: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseSlider.domSelectors.slidesAfterInit));

		dots.forEach((dot): void => {
			const dotId = Number(dot.getAttribute(BaseSlider.attributes.paginationIndex));
			if (dotId === this.currentSlide) {
				dot.setAttribute('tabindex', '-1');
				dot.classList.add(BaseSlider.stateClasses.isActive);
			} else {
				dot.removeAttribute('tabindex');
				dot.classList.remove(BaseSlider.stateClasses.isActive);
			}
		});

		slides.forEach((slide): void => {
			if (slide.classList.contains(BaseSlider.stateClasses.isSelected)) {
				TabIndexUtil.unlockTabulator(slide);
			} else {
				TabIndexUtil.lockTabulator(slide);
			}
		});

		if (dots.length > this.maxDotsVisible) {
			this.calculatePaginationDotsOverflow();
		}

		this.element.classList.remove(BaseSlider.stateClasses.hasSelectedFirstSlide);
		this.element.classList.remove(BaseSlider.stateClasses.hasSelectedLastSlide);

		if (this.currentSlide === 0) {
			this.element.classList.add(BaseSlider.stateClasses.hasSelectedFirstSlide);
		} else if (this.currentSlide && this.currentSlide + 1 === this.slideCount) {
			this.element.classList.add(BaseSlider.stateClasses.hasSelectedLastSlide);
		}
	}

	// pagination dots overflow instagram style

	private calculatePaginationDotsOverflow(): void {
		const dots: HTMLElement[] = Array.from(this.element.querySelectorAll(BaseSlider.domSelectors.paginationItem));
		const outer = this.element.querySelector(BaseSlider.domSelectors.paginationWrapperOuter) as HTMLElement;
		const inner = this.element.querySelector(BaseSlider.domSelectors.paginationWrapperInner) as HTMLElement;
		const firstInactiveDot = dots.find((dot): boolean => !dot.classList.contains(BaseSlider.stateClasses.isActive));

		// a certain markup structure has to be given for the pagination
		if (!outer || !inner || !firstInactiveDot) {
			return;
		}

		const selectedDot = dots.find((dot): boolean => dot.classList.contains(BaseSlider.stateClasses.isActive));
		const selectedIndex = selectedDot ? Number(selectedDot.getAttribute(BaseSlider.attributes.paginationIndex)) : 0;
		const firstInactiveDotComputedStyle = window.getComputedStyle(firstInactiveDot);
		const singleDotTotalHorizontalWidth =
			parseFloat(firstInactiveDotComputedStyle.width || '0') +
			parseFloat(firstInactiveDotComputedStyle.marginRight || '0') +
			parseFloat(firstInactiveDotComputedStyle.marginLeft || '0');
		let offset = null;

		// use overflow container if more than x dots
		if (dots.length > this.maxDotsVisible) {
			outer.classList.add(BaseSlider.stateClasses.hasOverflow);
			outer.style.width = `${singleDotTotalHorizontalWidth * this.maxDotsVisible}px`;
		}

		// calculate the new dot window and offset

		const isAtEndOfSection = selectedIndex > dots.length - this.dotDiff - 1;
		const isAtStartOfSection = selectedIndex < this.dotDiff;
		const isBiggerThenMax = selectedIndex > this.dotWindowMax;
		const isLowerThenMin = selectedIndex < this.dotWindowMin;

		if (isAtEndOfSection) {
			this.dotWindowMax = dots.length - this.dotDiff - 1;
			this.dotWindowMin = this.dotWindowMax - this.dotWindow + 1;
			offset = this.dotWindowMin - this.dotDiff;
		} else if (isAtStartOfSection) {
			this.dotWindowMax = this.dotDiff + this.dotWindow - 1;
			this.dotWindowMin = this.dotWindowMax - this.dotWindow + 1;
			offset = 0;
		} else if (isBiggerThenMax) {
			this.dotWindowMax = selectedIndex;
			this.dotWindowMin = this.dotWindowMax - this.dotWindow + 1;
			offset = this.dotWindowMin - this.dotDiff;
		} else if (isLowerThenMin) {
			this.dotWindowMax = selectedIndex + this.dotWindow - 1;
			this.dotWindowMin = this.dotWindowMax - this.dotWindow + 1;
			offset = this.dotWindowMin - this.dotDiff;
		}

		if (offset !== null) {
			inner.style.transform = `translateX(-${offset * singleDotTotalHorizontalWidth}px)`;
		}

		// set dot classes
		dots.forEach((dot, index): void => {
			dot.classList.remove(BaseSlider.stateClasses.hasMore);

			if (index > this.dotWindowMax + this.dotDiff - 1 && this.dotWindowMax !== dots.length - this.dotDiff - 1) {
				dot.classList.add(BaseSlider.stateClasses.hasMore);
			} else if (index < this.dotWindowMin - this.dotDiff + 1 && this.dotWindowMin !== this.dotDiff) {
				dot.classList.add(BaseSlider.stateClasses.hasMore);
			}
		});
	}

	/**
	 * call resize of all instances of flickity inside a given container because it gets initialized
	 * wrong if the content pane is set to display: none on page load
	 * useful for Accordion and Modals with Galleries inside
	 */
	public static resizeSliders(container: HTMLElement | Document = document): void {
		const sliders: HTMLElement[] = Array.from(container.querySelectorAll(BaseSlider.domSelectors.slidesWrapper));

		sliders.forEach((slider): void => {
			const flickity: Flickity = Flickity.data(slider);

			if (!flickity) return;

			flickity.resize();
		});
	}

	/**
	 * Adds flickity-resize class whenever flickity was resized
	 *
	 * https://github.com/metafizzy/flickity/issues/534#issuecomment-328859144
	 */
	private static addResizeClassesToFlickity(): void {
		// @ts-ignore
		if (Flickity.prototype.createResizeClass) {
			return;
		}

		// @ts-ignore
		// eslint-disable-next-line func-names
		Flickity.prototype.createResizeClass = function(): void {
			// @ts-ignore
			this.element.classList.add('flickity-resize');
		};

		// @ts-ignore
		Flickity.createMethods.push('createResizeClass');

		const resize: () => void = Flickity.prototype.resize;
		// eslint-disable-next-line func-names
		Flickity.prototype.resize = function(): void {
			// @ts-ignore
			this.element.classList.remove('flickity-resize');
			resize.call(this);
			// @ts-ignore
			this.element.classList.add('flickity-resize');
		};
	}
}

export interface IBaseSliderOptions {
	flickityOptions?: IFlickityOptions;
}
